import { Component, ElementRef, HostBinding, OnInit, ViewChild, OnDestroy, Inject } from '@angular/core';
import { Task } from '../../models/task';
import { UserstoryService } from '../../services/userstory.service';
import { SynchronizationService } from '../../services/synchronization.service';
import { Issue } from '../../models/issue';
import { Status } from '../../models/status';
import { Userstory } from '../../models/userstory';
import { Person } from '../../models/person';
import SelectableComponent from '../../interfaces/selectableComponent';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { HighlightTeammemberService } from '../../services/highlight-teammember.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { IssueType } from '../../models/enums/issueType';
import { SettingsService } from '../../services/settings.service';
import { EventIssuecardClosed } from '../../models/syncEvents/realtimeEvents/eventIssuecardClosed';
import { EventIssuecardUpdated } from '../../models/syncEvents/realtimeEvents/eventIssuecardUpdated';
import { EventIssueDeleted } from '../../models/syncEvents/persistenceEvents/eventIssueDeleted';
import { EventIssueCreated } from '../../models/syncEvents/persistenceEvents/eventIssueCreated';
import { EventIssueClosed } from '../../models/syncEvents/persistenceEvents/eventIssueClosed';
import { ComponentEvent } from '../../models/enums/componentEvent';
import { OnlineuserService } from '../../services/onlineuser.service';
import { Listener, CommunicationService } from '../../services/communication.service';
import { ProjectService } from '../../services/project.service';
import { SyncEventType } from '../../models/enums/syncEventType';
import { EventAllIssuesMarkedAsRead } from '../../models/syncEvents/persistenceEvents/eventAllIssuesMarkedAsRead';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-issuecard',
  templateUrl: './issuecard.component.html',
  styleUrls: ['./issuecard.component.scss']
  })
export class IssuecardComponent implements OnInit, OnDestroy, SelectableComponent, Listener<AwallComponentEvent>  {
  @ViewChild('dialogTitle') private dialogTitle: ElementRef;
  public _issue: Issue;

  // If the card has been removed from the droplist it is considered 'floating'
  private _issueIsFloating = true;
  public issueContainer: Array<any>;
  public issueHasBeenChanged = false;
  public formIsValid = true;
  // TODO this doesnt belong here
  public titleMsg: String = '';
  // TODO this doesnt belong here, move to translation-texts
  private TITLE_ERROR_MSG: String = 'The title is required.';
  // For new issues that are being created and dont have an id and corresponding jira-issue yet
  public isNewIssue = false;
  public originalIssue: Issue;
  public storyPointsOptions = [null, 1, 2, 3, 5, 8, 13, 21, 34];
  public componentRef;
  // TODO this will eventually need to be more dynamic, getting available types from the issue-tracking-api
  public availableIssueTypeKeys: string[] = ['USERSTORY', 'TASK', 'BUG', 'EPIC'];
  public issueTypeEnum = IssueType;
  public onCreateAssignToSprint = true;
  public avatars: string[] = [];
  public static readonly componentId = 'IssuecardComponent';
  private subscriptions: Subscription[] = [];

  @HostBinding('style.z-index')
  public zIndex;

  constructor(
    @Inject('issue') public issueParameter: Issue,
    @Inject('projectKey') public readonly projectKey: string,
    @Inject('statuses') public readonly statuses: Status[],
    @Inject('onCloseCallback') public readonly onCloseCallback,
    @Inject('onNewIssueCreatedCallback') public readonly onNewIssueCreatedCallback,
    @Inject('onNewSubIssueCreatedCallback') public readonly onNewSubIssueCreatedCallback,
    @Inject('onIssueRemovedCallback') public readonly onIssueRemovedCallback,
    @Inject('onSubIssueRemovedCallback') public readonly onSubIssueRemovedCallback,
    @Inject('onIssueModified') public readonly onIssueModified,
    protected userstoryService: UserstoryService,
    protected projectService: ProjectService,
    protected settingsService: SettingsService,
    protected communicationService: CommunicationService,
    protected syncService: SynchronizationService,
    protected elementRef: ElementRef,
    protected highlightService: HighlightTeammemberService,
    protected translate: TranslateService,
    protected dialog: MatDialog,
    protected onlineUserService: OnlineuserService,
    @Inject('avatar') public avatar?: string) {

    this.issue = issueParameter;
    this.updateAvatare(avatar);
  }

  static getContainer(): HTMLElement {
    return document.getElementById('issuecardContainer');
  }

  ngOnInit(): void {
    this.communicationService.registerComponent(IssuecardComponent.componentId, this);
    this.elementRef.nativeElement.setAttribute('data-issueid', this.issue.id);
    this.elementRef.nativeElement.setAttribute('data-parentissueid', this.issue.parentIssueId);
    this.initAllIssuesMarkedAsReadSubscription();
  }

  /**
   * Returns an ElementReference to the host-element of this component for DOM-access
   */
  getElementRef(): ElementRef {
    return this.elementRef;
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(IssuecardComponent.componentId);
    this.syncService.sendPersistenceEventNew(new EventIssueClosed(this.issue.id, this.onlineUserService.getAvatarUrl()));
    for (const event of this.subscriptions) {
      event.unsubscribe();
    }
  }

  private initAllIssuesMarkedAsReadSubscription(): void {
    this.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ALL_ISSUES_MARKED_AS_READ).subscribe(
      payload => {
        const event = EventAllIssuesMarkedAsRead.fromPayload(payload);
        this.issue.lastViewedInAwall = event.timestamp;
      }
    ));
    this.subscriptions.push(this.userstoryService.allIssuesMarkedAsRead$.subscribe(
      timestamp => {
        this.issue.lastViewedInAwall = timestamp;
      }
    ));
  }

  /**
   * Close this Issuecard. The corresponding issue will be moved back into its container and this issuecard destroyed.
   */
  closeThisCard(): void {
    if (this.issueHasBeenChanged) {
      this.saveChanges();
    }
    this.onCloseCallback(this);
    this.syncService.sendRealTimeEventNew(new EventIssuecardClosed(this.issue.id, this.onlineUserService.getAvatarUrl()));
  }

  discardChangesAndCloseThisCard(): void {
    this.discardChanges();
    this.onCloseCallback(this);
  }

  onDiscardButtonClicked(): void {
    this.discardChangesAndCloseThisCard();
    this.syncService.sendRealTimeEventNew(new EventIssuecardClosed(this.issue.id, this.onlineUserService.getAvatarUrl()));
  }

  update(updatedIssue: Issue): void {
    // We assume the issue will be changed. This might not actually be true if (updatedIssue == our issue)
    this.issueHasBeenChanged = true;
    this.issue.description = updatedIssue.description;
    this.issue.summary = updatedIssue.summary;
    this.issue.status = updatedIssue.status;
    this.issue.issueType = updatedIssue.issueType;
    this.issue.storyPoints = updatedIssue.storyPoints;
    this.issue.assignee = updatedIssue.assignee;
    this.issue.reporter = updatedIssue.reporter;

    this.validateForm();
  }

  deleteThisIssue(): void {
    this.userstoryService.deleteIssue(this.issue.id, this.projectKey).subscribe(() => {
      this.syncService.sendPersistenceEventNew(new EventIssueDeleted(this.issue.id, this.issue.isSubissue));
      this.closeThisCard();
      if (this.issue.isSubissue) {
        this.onSubIssueRemovedCallback(this.issue.id);
      } else {
        this.onIssueRemovedCallback(this.issue.id);
      }
    });
  }

  openConfirmDeleteDialog(event): void {
    const title = this.translate.instant('components.issuecard.deleteConfirmationTitle');
    const messageIssue = this.translate.instant('components.issuecard.deleteConfirmationTextIssue');
    const messageSubissue = this.translate.instant('components.issuecard.deleteConfirmationTextSubissue');
    const message = (this._issue.isSubissue) ? messageSubissue : messageIssue;
    const okText = this.translate.instant('components.issuecard.deleteConfirmationOkText');
    const cancelText = this.translate.instant('components.issuecard.deleteConfirmationCancelText');

    // The button stays highlighted after we dismiss the dialog. This does not look good so we
    // need to remove the focus manually here
    event.srcElement.closest('button').blur();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { title: title, message: message, buttonTextConfirm: okText, buttonTextCancel: cancelText }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.deleteThisIssue();
      }
    });
  }

  validateForm(): void {
    if (this.issue.summary === '') {
      this.formIsValid = false;
      this.titleMsg = this.TITLE_ERROR_MSG;
    } else {
      this.formIsValid = true;
      this.titleMsg = '';
    }
  }

  issueDescriptionChanged(event: any): void {
    this.issue.description = event.target.value;
    this.issueHasBeenChanged = true;
    this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue, this.onlineUserService.getAvatarUrl()));
    this.showTitleErrorMessage();
  }

  issueTitleChanged(event: any): void {
    this.issue.summary = event.target.value;
    this.issueHasBeenChanged = true;

    this.validateForm();
    this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue, this.onlineUserService.getAvatarUrl()));
  }

  issueStatusChanged(event: any): void {
    const status = this.statuses.find(s => (String(s.id) === event.target.value));
    this.issueHasBeenChanged = true;
    this.issue.status = status;
    this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue, this.onlineUserService.getAvatarUrl()));
    this.showTitleErrorMessage();
  }

  issueTypeChanged(event: any): void {
    const selectedIssueType: IssueType = Number(event.target.value);
    this.issueHasBeenChanged = true;
    this.issue.issueType = selectedIssueType;
    this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue, this.onlineUserService.getAvatarUrl()));
    this.showTitleErrorMessage();
  }

  issueStoryPointsChanged(event: any): void {
    this.issueHasBeenChanged = true;
    this.issue.storyPoints = Number(event.target.value);
    this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue, this.onlineUserService.getAvatarUrl()));
    this.showTitleErrorMessage();
  }

  issueAssigneeChanged(assignee: Person): void {
    this.issue.assignee = assignee;
    this.issueHasBeenChanged = true;
    this.updateHighlightColor();
    this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue, this.onlineUserService.getAvatarUrl()));
  }

  issueReporterChanged(reporter: Person) {
    this.issue.reporter = reporter;
    this.issueHasBeenChanged = true;
    this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue, this.onlineUserService.getAvatarUrl()));
  }

  /** Used for the Status-Select-Element. Allows us to use the status-object directly as value of the select. */
  compareStatus(s1: Status, s2: Status): boolean {
    return s1.id === s2.id;
  }

  showTitleErrorMessage(): void {
    if (!this.formIsValid) {
      this.titleMsg = this.TITLE_ERROR_MSG;
    }
  }

  saveChanges(): void {
    if (this.issueHasBeenChanged) {
      this.issueHasBeenChanged = false;

      if (this._issue instanceof Task) {
        this.userstoryService.updateTask(this.projectKey, <Task>this.issue);
      }
      if (this._issue instanceof Userstory) {
        // Only Userstories can have story points. Updating a non-userstory-issue with points will lead to an error on JIRA
        // The user needs to save an issue as Story first, before he can save storypoints.
        if (this.originalIssue.issueType !== this.issueTypeEnum.USERSTORY) {
          this.issue.storyPoints = null;
        }
        this.userstoryService.updateIssue(this.projectKey, this.issue.id, <Userstory>this.issue);
      }
      this.issue.updatedAt = new Date().toISOString();
      this.onIssueModified();
    }
  }

  /* This method is triggered when sync-event from another client is received. We DONT send a sync-event of our own */
  discardChanges(): void {
    this.issueHasBeenChanged = false;

    // TODO updating the whole issue loses the attachments/comments that are loaded after the issue
    this.issue.description = this.originalIssue.description;
    this.issue.summary = this.originalIssue.summary;
    this.issue.status = this.originalIssue.status;
    this.issue.storyPoints = this.originalIssue.storyPoints;
    this.issue.assignee = this.originalIssue.assignee;
    this.issue.reporter = this.originalIssue.reporter;
    this.issue.issueType = this.originalIssue.issueType;
  }

  confirmNewIssueCreation(): void {
    // If the parentIssueId is a valid one, we are dealing with a subissue here. If the parentIssueId is a temporary id (< 0)
    // its a new issue.
    const isSubissue = (this.issue.parentIssueId > 0);
    if (isSubissue) { this.createSubissue(); } else { this.createIssue(); }
  }

  private createIssue(): void {
    // TODO this will only work for daily view. We need a more flexible approach to create new issues
    // in sprintplanning (for backlog, no sprint set) or mpc (different sprints possible)
    const sprintId = this.settingsService.getSprintId(this.projectKey);
    const temporaryIssueId = this.issue.id;

    this.userstoryService.createIssue(
      this.projectKey,
      this.issue.issueType,
      this.issue.summary,
      this.issue.description,
      this.issue.assignee,
      this.issue.reporter,
      this.onCreateAssignToSprint ? this.issue.status.id : null,
      this.onCreateAssignToSprint ? sprintId : null,
      this.issue.storyPoints
    ).subscribe(createdIssueData => {
      if (createdIssueData) {
        const createdIssue: Issue = this.userstoryService.mapJSONToUserstory(createdIssueData, this.projectKey);
        this.issue = createdIssue;
        this.isNewIssue = false;
        this._issueIsFloating = true;
        this.onNewIssueCreatedCallback(createdIssue);

        const syncEvent = new EventIssueCreated(this.issue.id, temporaryIssueId, null, false);
        this.syncService.sendPersistenceEventNew(syncEvent);
      }
      else {
        console.log('issue could NOT be created.');
      }
      this.onCloseCallback(this);
    });
  }

  private createSubissue(): void {
    const temporaryIssueId = this.issue.id;

    this.userstoryService.createSubissue(
      this.projectKey,
      this.issue.parentIssueId,
      this.issue.summary,
      this.issue.description,
      this.issue.assignee,
      this.issue.reporter,
      this.issue.status.id,
    ).subscribe(createdSubissueData => {
      if (createdSubissueData) {
        const createdSubissue: Issue = this.userstoryService.mapJSONToTask(createdSubissueData, this.issue.parentIssueId, this.projectKey);
        this.issue = createdSubissue;
        this.isNewIssue = false;
        this._issueIsFloating = true;
        this.onNewSubIssueCreatedCallback(createdSubissue);

        const syncEvent = new EventIssueCreated(this.issue.id, temporaryIssueId, createdSubissue.parentIssueId, true);
        this.syncService.sendPersistenceEventNew(syncEvent);
      }
      else {
        console.log('issue could NOT be created.');
      }
      this.onCloseCallback(this);
    });
  }

  updateExtendedData(): void {
    this.updateAttachments();
    this.updateComments();
  }

  private updateAttachments(): void {
    this.userstoryService.getAttachmentsForIssue(this.projectKey, this._issue.id).subscribe(data => {
      this._issue.attachments = data;
    });
  }

  private updateComments(): void {
    this.userstoryService.getCommentsForIssue(this.projectKey, this._issue.id).subscribe(data => {
      this._issue.comments = data;
    });
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) { return; }

    switch (event.name) {
      case ComponentEvent.WIDGET_TEAM_HIGHLIGHT_CHANGED: {
        const person: Person = <Person>event.value['person'];
        const colorName: string = event.value['colorName'];
        this.setHighlightColor(person, colorName);
        break;
      }
    }
  }

  private setHighlightColor(person: Person, colorName: string): void {
    if (this.issue.assignee && this.issue.assignee.id === person.id) {
      (<HTMLElement>this.getElementRef().nativeElement).setAttribute('data-highlightcolor', colorName);
    }
  }

  private updateHighlightColor(): void {
    this.setHighlightColor(this.issue.assignee, this.highlightService.getHighlightColorName(this.issue.assignee));
  }

  /**
   * Sets the issue for this issuecard and triggers loading of additional task information (attachments & comments)
   */
  public set issue(issue: Issue) {
    this._issue = issue;
    this.originalIssue = this._issue.getCopy();
    // New, not yet persisted issues have a temporary id which is a negative number
    this.isNewIssue = (this.issue.id < 0);
    this.updateHighlightColor();
    if (!this.isNewIssue) {
      this.updateExtendedData();
    }
  }

  public get issue(): Issue {
    return this._issue;
  }

  // TODO this should not be neccessary anymore, check usage
  public set issueIsFloating(isFloating: boolean) {
    this._issueIsFloating = isFloating;
  }

  // TODO this should not be neccessary anymore, check usage
  public get issueIsFloating(): boolean {
    return this._issueIsFloating;
  }
  public updateAvatare(avatar: string) {
    if (this.avatars.filter(avatarList => avatarList === avatar).length === 0 && avatar !== '') {
      this.avatars.push(avatar);
    }
  }
  public removeAvatar(avatar: string) {
    this.avatars = this.avatars.filter(avatarList => avatarList !== avatar);
  }
}
